package com.dotdrives.web.pages.marketing

import com.dotdrives.design.DotButtonVariant
import com.dotdrives.design.DotTone
import com.dotdrives.web.ui.DotButton
import com.dotdrives.web.ui.DotPill
import react.FC
import react.Key
import react.Props
import react.dom.html.ReactHTML.br
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.a
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.footer
import react.dom.html.ReactHTML.h1
import react.dom.html.ReactHTML.h2
import react.dom.html.ReactHTML.nav
import react.dom.html.ReactHTML.p
import react.dom.html.ReactHTML.section
import react.dom.html.ReactHTML.span
import web.cssom.ClassName
import web.html.ButtonType
import web.html.button as buttonType

// ---- copy, verbatim from design/marketing-landing.dc.html ----

private data class Phase(val name: String, val hint: String)

private val phases = listOf(
    Phase("Identify", "Who could give?"),
    Phase("Connect", "Build the relationship"),
    Phase("Ask", "Make the invitation"),
    Phase("Close", "Confirm the gift"),
    Phase("Steward", "Say thank you, often"),
)

private val trustedLogos = listOf(
    "Hope Collective",
    "Riverbend Missions",
    "The Water Project",
    "BrightPath Youth",
    "Second Harvest",
    "Cornerstone Fund",
)

private data class ProcessPoint(val bold: String, val text: String)

private val processPoints = listOf(
    ProcessPoint("Phase templates", "start from the proven five phases or design your own process per campaign."),
    ProcessPoint("Tasks & reminders", "each phase carries a checklist, and next steps land on your dashboard and inbox."),
    ProcessPoint("Goals & budgets", "personal goals and campaign budgets keep the whole team honest."),
)

private data class KanbanCard(val name: String, val sub: String, val color: String)
private data class KanbanColumn(val name: String, val count: String, val cards: List<KanbanCard>)

private val kanbanColumns = listOf(
    KanbanColumn(
        "Connect",
        "4",
        listOf(
            KanbanCard("The Miller Family", "$25k · Clean Water", "#4696EC"),
            KanbanCard("J. & A. Whitfield", "$10k · General Fund", "#4696EC"),
        ),
    ),
    KanbanColumn("Ask", "2", listOf(KanbanCard("Grace Chapel", "$8k · Youth Camp", "#F26223"))),
    KanbanColumn(
        "Close",
        "3",
        listOf(
            KanbanCard("Hendersons", "$15k · pledged ✓", "#00A65A"),
            KanbanCard("R. Okafor", "$5k · pledged ✓", "#00A65A"),
        ),
    ),
)

private data class Feature(val icon: String, val bg: String, val name: String, val detail: String)

private val features = listOf(
    Feature("🧭", "#F26223", "Drives", "Visual pipelines that keep every donor moving — drag, drop, done."),
    Feature("👥", "#4696EC", "Donor management", "Profiles, notes, files, history, and CSV import with duplicate catching."),
    Feature("💛", "#00A65A", "Giving forms", "Branded online giving pages — one-time or recurring, live in minutes."),
    Feature("🔄", "#2CA01C", "QuickBooks sync", "Every gift books itself. Customers, items, and classes stay mapped."),
    Feature("🎯", "#B8491A", "Goals & budgets", "Org budgets, campaign budgets, and personal goals in one view."),
    Feature("📣", "#5B4AA8", "Updates & reports", "Field updates, donor reports, and board-ready summaries."),
)

private data class FeedEntry(
    val initials: String,
    val bg: String,
    val name: String,
    val form: String,
    val amount: String,
    val whenText: String,
)

private val feed = listOf(
    FeedEntry("MK", "#4696EC", "Maria K.", "Clean Water Fund", "+$250", "JUST NOW"),
    FeedEntry("DT", "#F26223", "Daniel T.", "Monthly partner", "+$75/mo", "2 MIN AGO"),
    FeedEntry("SB", "#00A65A", "Sarah B.", "Youth Camp 2026", "+$500", "11 MIN AGO"),
    FeedEntry("LN", "#5B4AA8", "The Nguyens", "General Fund", "+$1,200", "26 MIN AGO"),
)

private data class DayMoment(
    val time: String,
    val cardIcon: String,
    val cardBg: String,
    val cardFg: String,
    val cardTitle: String,
    val cardBody: String,
    val title: String,
    val body: String,
)

private val dayMoments = listOf(
    DayMoment(
        time = "8:04 AM · COFFEE",
        cardIcon = "1️⃣",
        cardBg = "#FEF8F6",
        cardFg = "#B8491A",
        cardTitle = "Call the Millers — match expires",
        cardBody = "Ranked #1 of your 3 moves today",
        title = "Open to a plan, not a pile",
        body = "The dashboard ranks your day from overdue steps, quiet drives, and unthanked gifts. Three moves, done by lunch.",
    ),
    DayMoment(
        time = "12:30 PM · AFTER LUNCH",
        cardIcon = "🎙",
        cardBg = "#fff",
        cardFg = "#222D32",
        cardTitle = "\"Ruth wants the timeline by Friday…\"",
        cardBody = "Voice note → giver note + next step, filed",
        title = "Capture it before the car starts",
        body = "Thirty seconds of talking becomes a note on the Millers and a reminder for Thursday. Nothing lives in your head.",
    ),
    DayMoment(
        time = "9:12 PM · GIFT LANDS",
        cardIcon = "💛",
        cardBg = "#E3F1E8",
        cardFg = "#007942",
        cardTitle = "$15,000 received — thank-you queued",
        cardBody = "Synced to QuickBooks · receipt sent",
        title = "The loop closes itself",
        body = "A gift books itself, receipts itself, and queues the thank-you. You just show up warm on the phone.",
    ),
)

private data class RoleCard(
    val icon: String,
    val bg: String,
    val name: String,
    val desc: String,
    val chip: String,
    val chipTone: DotTone,
)

private val roles = listOf(
    RoleCard("🧭", "#FDE5DA", "Fundraisers", "Their drives, their givers, their next steps.", "FULL ACCESS", DotTone.Primary),
    RoleCard("📣", "#E3F1E8", "Field representatives", "Post updates from anywhere — even by email.", "UPDATES ONLY", DotTone.Success),
    RoleCard("📊", "#EFF1F4", "Board members", "Clean read-only reports, no login wrangling.", "READ-ONLY", DotTone.Neutral),
    RoleCard("🧾", "#E4EEFB", "Finance", "Gifts reconcile to QuickBooks on their own.", "DONATIONS + QBO", DotTone.Info),
)

private data class Stat(val value: String, val label: String)

private val stats = listOf(
    Stat("32%", "more gifts closed in year one"),
    Stat("6 hrs", "saved weekly on data entry"),
    Stat("2×", "donor retention with stewarding"),
    Stat("10 min", "to your first live giving form"),
)

private data class Quote(
    val initials: String,
    val bg: String,
    val name: String,
    val role: String,
    val text: String,
    val shortText: String? = null,
    val desktopOnly: Boolean = false,
)

private val quotes = listOf(
    Quote(
        initials = "KH",
        bg = "#4696EC",
        name = "Kate H.",
        role = "Development Director, Riverbend Missions",
        text = "I stopped managing a spreadsheet and started managing relationships. Our team finally knows what 'next' means for every single donor.",
        shortText = "I stopped managing a spreadsheet and started managing relationships. Our team finally knows what 'next' means for every donor.",
    ),
    Quote(
        initials = "MO",
        bg = "#00A65A",
        name = "Miguel O.",
        role = "Executive Director, BrightPath Youth",
        text = "The field reps actually use it — because it only shows them their piece. Updates come in with photos now instead of chasing emails.",
        desktopOnly = true,
    ),
    Quote(
        initials = "JT",
        bg = "#B8491A",
        name = "Jana T.",
        role = "Finance Lead, Hope Collective",
        text = "QuickBooks sync alone pays for it. Month-end went from a weekend of matching receipts to a ten-minute review.",
        shortText = "QuickBooks sync alone pays for it. Month-end went from a weekend to ten minutes.",
    ),
)

private data class Faq(val question: String, val answer: String?, val mark: String)

private val faqs = listOf(
    Faq(
        "Can we bring our donors over from a spreadsheet or another CRM?",
        "Yes — CSV import with a ready-made template, smart column matching, and duplicate detection so nobody gets imported twice.",
        "–",
    ),
    Faq("Do we have to use your five phases?", null, "+"),
    Faq("What does the QuickBooks sync actually do?", null, "+"),
    Faq("Is there a limit on users or donors?", null, "+"),
    Faq("What happens after the 14-day trial?", null, "+"),
)

private data class PricingPlan(
    val name: String,
    val price: String,
    val per: String,
    val sub: String,
    val bg: String,
    val fg: String,
    val btnVariant: DotButtonVariant,
    val cta: String,
    val features: List<String>,
)

private val plans = listOf(
    PricingPlan(
        name = "Standard",
        price = "$99",
        per = "/ month",
        sub = "For teams getting their process out of spreadsheets.",
        bg = "#FFFDFA",
        fg = "#222D32",
        btnVariant = DotButtonVariant.Tinted,
        cta = "Start free trial",
        features = listOf(
            "Unlimited drives & givers",
            "Giving forms + Stripe payments",
            "Reminders & goals",
            "CSV import / export",
            "Email support",
        ),
    ),
    PricingPlan(
        name = "Enterprise",
        price = "$249",
        per = "/ month",
        sub = "For organizations with field staff and finance to keep happy.",
        bg = "#222D32",
        fg = "#ffffff",
        btnVariant = DotButtonVariant.Primary,
        cta = "Schedule a demo",
        features = listOf(
            "Everything in Standard",
            "QuickBooks Online sync",
            "Field representative updates",
            "Roles & granular permissions",
            "Priority support & onboarding",
        ),
    ),
)

// ---- style helpers ----

private fun bgStyle(color: String): dynamic {
    val s = js("({})")
    s.backgroundColor = color
    return s
}

private fun borderLeftStyle(color: String): dynamic {
    val s = js("({})")
    s.borderLeftColor = color
    return s
}

private fun fgStyle(color: String): dynamic {
    val s = js("({})")
    s.color = color
    return s
}

private fun cardStyle(bg: String, fg: String? = null): dynamic {
    val s = js("({})")
    s.backgroundColor = bg
    if (fg != null) s.color = fg
    return s
}

// ---- shared bits ----

external interface MarketingCtaProps : Props {
    var onStartTrial: () -> Unit
}

external interface MarketingNavProps : Props {
    var onSignIn: () -> Unit
    var onStartTrial: () -> Unit
}

private val MarketingBrandMark = FC<Props> {
    span {
        className = ClassName("marketing-nav-brand-mark")
        ariaHidden = true
        +"•••"
    }
}

// ---- nav ----

val MarketingNav = FC<MarketingNavProps> { props ->
    nav {
        className = ClassName("marketing-nav")
        a {
            href = "#/"
            ariaLabel = "Dot Drives home"
            className = ClassName("marketing-nav-brand dot-home-link")
            MarketingBrandMark()
            span { +"dot drives" }
        }
        div {
            className = ClassName("marketing-nav-links")
            span { +"Product" }
            span { +"Pricing" }
            span { +"Stories" }
            span { +"About" }
        }
        div {
            className = ClassName("marketing-nav-full-actions")
            button {
                type = ButtonType.buttonType
                className = ClassName("marketing-nav-signin")
                onClick = { props.onSignIn() }
                +"Sign in"
            }
            DotButton {
                text = "Try it free"
                onClick = props.onStartTrial
                variant = DotButtonVariant.Primary
                className = "marketing-nav-cta"
            }
        }
        div {
            className = ClassName("marketing-nav-compact")
            button {
                type = ButtonType.buttonType
                className = ClassName("marketing-nav-signin")
                onClick = { props.onSignIn() }
                +"Sign in"
            }
            DotButton {
                text = "Try free"
                onClick = props.onStartTrial
                variant = DotButtonVariant.Primary
                className = "marketing-nav-cta"
            }
        }
    }
}

// ---- hero ----

val MarketingHero = FC<MarketingCtaProps> { props ->
    section {
        className = ClassName("marketing-hero")
        div {
            className = ClassName("marketing-eyebrow marketing-hero-eyebrow")
            +"THE DONOR CRM THAT MOVES"
        }
        h1 {
            className = ClassName("marketing-hero-title")
            +"Every donor has a "
            br { className = ClassName("marketing-hero-break") }
            +"next step. "
            span {
                className = ClassName("marketing-hero-accent")
                +"See it."
            }
        }
        p {
            className = ClassName("marketing-hero-subcopy marketing-hero-subcopy-full")
            +(
                "Most CRMs were built for data entry. Dot Drives was built by fundraisers, for the actual work — " +
                    "moving real relationships toward a warm, confident ask."
                )
        }
        p {
            className = ClassName("marketing-hero-subcopy marketing-hero-subcopy-short")
            +(
                "Built by fundraisers, for the actual work — moving real relationships toward a warm, " +
                    "confident ask."
                )
        }
        div {
            className = ClassName("marketing-hero-actions")
            DotButton {
                text = "Schedule a demo"
                onClick = props.onStartTrial
                variant = DotButtonVariant.Ink
                className = "marketing-hero-cta"
            }
            DotButton {
                text = "Start free — no card"
                onClick = props.onStartTrial
                variant = DotButtonVariant.Tinted
                className = "marketing-hero-cta"
            }
        }
        MarketingDriveCard()
    }
}

private val MarketingDriveCard = FC<Props> {
    div {
        className = ClassName("marketing-drive-card")
        div {
            className = ClassName("marketing-drive-head")
            div {
                className = ClassName("marketing-drive-name")
                +"The Miller Family · Clean Water Fund"
            }
            div {
                className = ClassName("marketing-drive-meta")
                +"DRIVE #142 · $25,000 PROPOSED"
            }
        }
        div {
            className = ClassName("marketing-drive-track")
            div { className = ClassName("marketing-drive-dot") }
        }
        div {
            className = ClassName("marketing-phase-grid")
            phases.forEachIndexed { index, phase ->
                div {
                    key = "phase-$index".unsafeCast<Key>()
                    className = ClassName("marketing-phase")
                    div {
                        className = ClassName("marketing-phase-name")
                        +phase.name
                    }
                    div {
                        className = ClassName("marketing-phase-hint")
                        +phase.hint
                    }
                }
            }
        }
    }
}

// ---- trust strip ----

val MarketingTrustStrip = FC<Props> {
    section {
        className = ClassName("marketing-trust")
        div {
            className = ClassName("marketing-eyebrow marketing-eyebrow-muted")
            +"TRUSTED BY TEAMS RAISING REAL MONEY"
        }
        div {
            className = ClassName("marketing-trust-row")
            trustedLogos.forEachIndexed { index, logo ->
                div {
                    key = "logo-$index".unsafeCast<Key>()
                    className = ClassName("marketing-trust-logo")
                    +logo
                }
            }
        }
    }
}

// ---- process + kanban ----

val MarketingProcessSection = FC<Props> {
    section {
        className = ClassName("marketing-process")
        div {
            div {
                className = ClassName("marketing-eyebrow")
                +"DRIVES, NOT SPREADSHEETS"
            }
            h2 {
                className = ClassName("marketing-section-title")
                +"A proven process, already baked in"
            }
            p {
                className = ClassName("marketing-section-copy marketing-process-copy-full")
                +(
                    "Drives are our version of pipelines — every donor relationship gets a lane, a phase, and a " +
                        "clear next step. Drag a card forward and the whole team knows exactly where things stand."
                    )
            }
            p {
                className = ClassName("marketing-section-copy marketing-process-copy-short")
                +(
                    "Every donor relationship gets a lane, a phase, and a clear next step. Drag a card forward " +
                        "and the whole team knows where things stand."
                    )
            }
            div {
                className = ClassName("marketing-process-points")
                processPoints.forEachIndexed { index, point ->
                    div {
                        key = "process-point-$index".unsafeCast<Key>()
                        className = ClassName("marketing-process-point")
                        div {
                            className = ClassName("marketing-process-check")
                            ariaHidden = true
                            +"✓"
                        }
                        div {
                            className = ClassName("marketing-process-text")
                            span {
                                className = ClassName("marketing-process-point-bold")
                                +point.bold
                            }
                            +" — ${point.text}"
                        }
                    }
                }
            }
        }
        MarketingKanban()
    }
}

private val MarketingKanban = FC<Props> {
    div {
        className = ClassName("marketing-kanban")
        div {
            className = ClassName("marketing-kanban-grid")
            kanbanColumns.forEachIndexed { colIndex, column ->
                div {
                    key = "kanban-col-$colIndex".unsafeCast<Key>()
                    className = ClassName("marketing-kanban-col")
                    div {
                        className = ClassName("marketing-kanban-col-head")
                        div {
                            className = ClassName("marketing-kanban-col-name")
                            +column.name
                        }
                        div {
                            className = ClassName("marketing-kanban-col-count")
                            +column.count
                        }
                    }
                    column.cards.forEachIndexed { cardIndex, card ->
                        div {
                            key = "kanban-card-$colIndex-$cardIndex".unsafeCast<Key>()
                            className = ClassName("marketing-kanban-card")
                            style = borderLeftStyle(card.color)
                            div {
                                className = ClassName("marketing-kanban-card-name")
                                +card.name
                            }
                            div {
                                className = ClassName("marketing-kanban-card-sub")
                                +card.sub
                            }
                        }
                    }
                }
            }
        }
        div {
            className = ClassName("marketing-kanban-float")
            div {
                className = ClassName("marketing-kanban-card-name")
                +"Ortiz Foundation"
            }
            div {
                className = ClassName("marketing-kanban-card-sub")
                +"$50k · moving up ↗"
            }
        }
    }
}

// ---- features ----

val MarketingFeaturesSection = FC<Props> {
    section {
        className = ClassName("marketing-features")
        div {
            className = ClassName("marketing-features-head")
            div {
                className = ClassName("marketing-eyebrow marketing-eyebrow-inverted")
                +"EVERYTHING YOU NEED. NOTHING YOU DON'T."
            }
            h2 {
                className = ClassName("marketing-features-title")
                +"One home for the whole fundraise"
            }
        }
        div {
            className = ClassName("marketing-features-grid")
            features.forEachIndexed { index, feature ->
                div {
                    key = "feature-$index".unsafeCast<Key>()
                    className = ClassName("marketing-feature-card")
                    div {
                        className = ClassName("marketing-feature-icon")
                        style = bgStyle(feature.bg)
                        ariaHidden = true
                        +feature.icon
                    }
                    div {
                        div {
                            className = ClassName("marketing-feature-name")
                            +feature.name
                        }
                        div {
                            className = ClassName("marketing-feature-detail")
                            +feature.detail
                        }
                    }
                }
            }
        }
    }
}

// ---- giving forms + quickbooks ----

val MarketingGivingFormsSection = FC<Props> {
    section {
        className = ClassName("marketing-forms")
        div {
            div {
                className = ClassName("marketing-eyebrow")
                +"GIVING FORMS + QUICKBOOKS"
            }
            h2 {
                className = ClassName("marketing-section-title marketing-section-title-sm")
                +"Gifts land themselves"
            }
            p {
                className = ClassName("marketing-section-copy marketing-section-copy-tight")
                +(
                    "Publish a branded giving page in minutes. One-time or recurring, every gift flows straight " +
                        "into the donor record — and syncs itself to QuickBooks. No Monday-morning spreadsheet " +
                        "reconciliation. Ever again."
                    )
            }
        }
        MarketingFeedPanel()
    }
}

private val MarketingFeedPanel = FC<Props> {
    div {
        className = ClassName("marketing-feed-panel")
        div {
            className = ClassName("marketing-feed-label")
            +"LIVE SUBMISSION FEED"
        }
        div {
            className = ClassName("marketing-feed-list")
            feed.forEachIndexed { index, entry ->
                div {
                    key = "feed-$index".unsafeCast<Key>()
                    className = ClassName("marketing-feed-row")
                    div {
                        className = ClassName("marketing-feed-person")
                        div {
                            className = ClassName("marketing-feed-avatar")
                            style = bgStyle(entry.bg)
                            +entry.initials
                        }
                        div {
                            div {
                                className = ClassName("marketing-feed-name")
                                +entry.name
                            }
                            div {
                                className = ClassName("marketing-feed-form")
                                +entry.form
                            }
                        }
                    }
                    div {
                        className = ClassName("marketing-feed-amount")
                        div {
                            className = ClassName("marketing-feed-amt")
                            +entry.amount
                        }
                        div {
                            className = ClassName("marketing-feed-when")
                            +entry.whenText
                        }
                    }
                }
            }
        }
        div {
            className = ClassName("marketing-feed-footer")
            span {
                className = ClassName("marketing-feed-synced")
                +"Synced to QuickBooks"
            }
            DotPill {
                text = "✓ up to date"
                tone = DotTone.Success
                className = "marketing-feed-badge"
            }
        }
    }
}

// ---- a day with dot drives ----

val MarketingDaySection = FC<Props> {
    section {
        className = ClassName("marketing-day")
        div {
            className = ClassName("marketing-day-head")
            div {
                className = ClassName("marketing-eyebrow marketing-eyebrow-center")
                +"A DAY WITH DOT DRIVES"
            }
            h2 {
                className = ClassName("marketing-day-title")
                +"It does the remembering. You do the relationships."
            }
        }
        div {
            className = ClassName("marketing-day-grid")
            dayMoments.forEachIndexed { index, moment ->
                div {
                    key = "day-$index".unsafeCast<Key>()
                    className = ClassName("marketing-day-card")
                    div {
                        className = ClassName("marketing-day-time")
                        +moment.time
                    }
                    div {
                        className = ClassName("marketing-day-mini")
                        style = bgStyle(moment.cardBg)
                        div {
                            className = ClassName("marketing-day-mini-head")
                            span {
                                className = ClassName("marketing-day-mini-icon")
                                ariaHidden = true
                                +moment.cardIcon
                            }
                            span {
                                className = ClassName("marketing-day-mini-title")
                                style = fgStyle(moment.cardFg)
                                +moment.cardTitle
                            }
                        }
                        div {
                            className = ClassName("marketing-day-mini-body")
                            +moment.cardBody
                        }
                    }
                    div {
                        className = ClassName("marketing-day-card-title")
                        +moment.title
                    }
                    div {
                        className = ClassName("marketing-day-card-body")
                        +moment.body
                    }
                }
            }
        }
    }
}

// ---- roles ----

val MarketingRolesSection = FC<Props> {
    section {
        className = ClassName("marketing-roles")
        div {
            div {
                className = ClassName("marketing-eyebrow")
                +"ONE TEAM, RIGHT-SIZED VIEWS"
            }
            h2 {
                className = ClassName("marketing-section-title")
                +"Everyone on the same page — literally"
            }
            p {
                className = ClassName("marketing-section-copy")
                +(
                    "Fundraisers see their drives. Field reps file updates from anywhere — no donor financials " +
                        "in sight. Boards get clean read-only reports. Finance gets books that reconcile themselves."
                    )
            }
            div {
                className = ClassName("marketing-role-list")
                roles.forEachIndexed { index, role ->
                    div {
                        key = "role-$index".unsafeCast<Key>()
                        className = ClassName("marketing-role-row")
                        div {
                            className = ClassName("marketing-role-icon")
                            style = bgStyle(role.bg)
                            ariaHidden = true
                            +role.icon
                        }
                        div {
                            className = ClassName("marketing-role-copy")
                            div {
                                className = ClassName("marketing-role-name")
                                +role.name
                            }
                            div {
                                className = ClassName("marketing-role-desc")
                                +role.desc
                            }
                        }
                        DotPill {
                            text = role.chip
                            tone = role.chipTone
                            className = "marketing-role-chip"
                        }
                    }
                }
            }
        }
        MarketingJosephCard()
    }
}

private val MarketingJosephCard = FC<Props> {
    div {
        className = ClassName("marketing-joseph")
        div {
            className = ClassName("marketing-joseph-label")
            +"WHAT JOSEPH (FIELD REP) SEES"
        }
        div {
            className = ClassName("marketing-joseph-card")
            div {
                className = ClassName("marketing-joseph-card-title")
                +"📣 Post an update"
            }
            div {
                className = ClassName("marketing-joseph-card-body")
                +"Photos + progress from the well site — even by email."
            }
        }
        div {
            className = ClassName("marketing-joseph-card")
            div {
                className = ClassName("marketing-joseph-card-title")
                +"✓ My reminders"
            }
            div {
                className = ClassName("marketing-joseph-card-body")
                +"Monthly report due Friday — that's it, that's the list."
            }
        }
        div {
            className = ClassName("marketing-joseph-card marketing-joseph-card-locked")
            div {
                className = ClassName("marketing-joseph-card-title")
                +"🔒 Donor financials"
            }
            div {
                className = ClassName("marketing-joseph-card-body")
                +"Not his job — so not on his screen."
            }
        }
        div {
            className = ClassName("marketing-joseph-footer")
            +"Granular read/write permissions per module — Admin, Standard, Field Rep, Read-only."
        }
    }
}

// ---- stats + testimonials ----

val MarketingStatsAndTestimonials = FC<Props> {
    section {
        className = ClassName("marketing-social")
        div {
            className = ClassName("marketing-stats-grid")
            stats.forEachIndexed { index, stat ->
                div {
                    key = "stat-$index".unsafeCast<Key>()
                    div {
                        className = ClassName("marketing-stat-value")
                        +stat.value
                    }
                    div {
                        className = ClassName("marketing-stat-label")
                        +stat.label
                    }
                }
            }
        }
        div {
            className = ClassName("marketing-quotes-grid")
            quotes.forEachIndexed { index, quote ->
                div {
                    key = "quote-$index".unsafeCast<Key>()
                    className = ClassName(
                        "marketing-quote-card" + if (quote.desktopOnly) " marketing-quote-desktop-only" else "",
                    )
                    div {
                        className = ClassName("marketing-quote-stars")
                        ariaHidden = true
                        +"★★★★★"
                    }
                    if (quote.shortText != null) {
                        div {
                            className = ClassName("marketing-quote-text marketing-quote-full")
                            +"\"${quote.text}\""
                        }
                        div {
                            className = ClassName("marketing-quote-text marketing-quote-short")
                            +"\"${quote.shortText}\""
                        }
                    } else {
                        div {
                            className = ClassName("marketing-quote-text")
                            +"\"${quote.text}\""
                        }
                    }
                    div {
                        className = ClassName("marketing-quote-footer")
                        div {
                            className = ClassName("marketing-quote-avatar")
                            style = bgStyle(quote.bg)
                            +quote.initials
                        }
                        div {
                            div {
                                className = ClassName("marketing-quote-name")
                                +quote.name
                            }
                            div {
                                className = ClassName("marketing-quote-role")
                                +quote.role
                            }
                        }
                    }
                }
            }
        }
    }
}

// ---- pricing ----

val MarketingPricingSection = FC<MarketingCtaProps> { props ->
    section {
        className = ClassName("marketing-pricing")
        div {
            className = ClassName("marketing-eyebrow marketing-eyebrow-center")
            +"SIMPLE PRICING"
        }
        h2 {
            className = ClassName("marketing-pricing-title")
            +"Priced like a tool, not a tax"
        }
        div {
            className = ClassName("marketing-pricing-grid")
            plans.forEachIndexed { index, plan ->
                div {
                    key = "plan-$index".unsafeCast<Key>()
                    className = ClassName("marketing-plan")
                    style = cardStyle(plan.bg, plan.fg)
                    div {
                        className = ClassName("marketing-plan-name")
                        +plan.name
                    }
                    div {
                        className = ClassName("marketing-plan-price-row")
                        span {
                            className = ClassName("marketing-plan-price")
                            +plan.price
                        }
                        span {
                            className = ClassName("marketing-plan-per")
                            +" ${plan.per}"
                        }
                    }
                    div {
                        className = ClassName("marketing-plan-sub")
                        +plan.sub
                    }
                    div {
                        className = ClassName("marketing-plan-feats")
                        plan.features.forEachIndexed { featIndex, feat ->
                            div {
                                key = "plan-$index-feat-$featIndex".unsafeCast<Key>()
                                className = ClassName("marketing-plan-feat")
                                span {
                                    className = ClassName("marketing-plan-feat-mark")
                                    ariaHidden = true
                                    +"·"
                                }
                                +feat
                            }
                        }
                    }
                    DotButton {
                        text = plan.cta
                        onClick = props.onStartTrial
                        variant = plan.btnVariant
                        className = "marketing-plan-cta"
                    }
                }
            }
        }
        div {
            className = ClassName("marketing-pricing-note marketing-pricing-note-full")
            +"14-day free trial on every plan · no setup fees · cancel anytime"
        }
        div {
            className = ClassName("marketing-pricing-note marketing-pricing-note-short")
            +"14-day free trial · no setup fees · cancel anytime"
        }
    }
}

// ---- faq ----

val MarketingFaqSection = FC<Props> {
    section {
        className = ClassName("marketing-faq")
        h2 {
            className = ClassName("marketing-faq-title")
            +"Fair questions"
        }
        div {
            className = ClassName("marketing-faq-list")
            faqs.forEachIndexed { index, faq ->
                div {
                    key = "faq-$index".unsafeCast<Key>()
                    className = ClassName("marketing-faq-item")
                    div {
                        className = ClassName("marketing-faq-row")
                        div {
                            className = ClassName("marketing-faq-question")
                            +faq.question
                        }
                        span {
                            className = ClassName("marketing-faq-mark")
                            ariaHidden = true
                            +faq.mark
                        }
                    }
                    if (faq.answer != null) {
                        div {
                            className = ClassName("marketing-faq-answer")
                            +faq.answer
                        }
                    }
                }
            }
        }
    }
}

// ---- closing cta ----

val MarketingClosingCta = FC<MarketingCtaProps> { props ->
    section {
        className = ClassName("marketing-cta")
        h2 {
            className = ClassName("marketing-cta-title")
            +"Your donors are waiting on a next step."
        }
        p {
            className = ClassName("marketing-cta-copy")
            +"Set up your first Drive in under ten minutes."
        }
        DotButton {
            text = "Start your free trial"
            onClick = props.onStartTrial
            variant = DotButtonVariant.Ink
            className = "marketing-cta-button"
        }
    }
}

// ---- footer ----

val MarketingFooter = FC<Props> {
    footer {
        className = ClassName("marketing-footer")
        a {
            href = "#/"
            ariaLabel = "Dot Drives home"
            className = ClassName("marketing-footer-brand dot-home-link")
            span {
                className = ClassName("marketing-footer-brand-mark")
                ariaHidden = true
                +"•••"
            }
            span { +"dot drives" }
        }
        div {
            className = ClassName("marketing-footer-links")
            span { +"Product" }
            span { +"Pricing" }
            span { +"About" }
            span { +"Contact" }
            span { +"Privacy" }
            span { +"Terms" }
        }
        div {
            className = ClassName("marketing-footer-copyright")
            +"© 2026 Dot Drives"
        }
    }
}
